import { RowGuide } from '@/app/equipmentTypes/CatalogTab';
import { SpaceFilterPills, useSpaceFilteredRows } from '@/app/equipmentTypes/SpaceFilterPills';
import AppIcon from '@/components/AppIcon';
import { InfoBadge } from '@/components/badges/Common';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { spacePillLabel } from '@/lib/spaceLabels';
import { allEquipmentTypesState, flowsByTypeIdState, spacesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { formatDate } from '@/util/dates';
import type { EquipmentType, EquipmentTypeId, SpaceId } from '@rock/types';
import { orderBy } from 'lodash';
import type { FC } from 'react';
import { Link } from 'react-router-dom';

/** Types retired out of the catalog, newest first. Each opens its usual page, guides and Restore included. */
export const RetiredTab: FC<{ types: EquipmentType[]; loaded: boolean }> = ({ types, loaded }) => {
    const visibleTypes = useSpaceFilteredRows(types);

    return (
        <>
            <SpaceFilterPills rows={types} />
            {!loaded ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : visibleTypes.length === 0 ? (
                <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
                    Nothing retired here.
                </div>
            ) : (
                <RetiredTable types={visibleTypes} />
            )}
        </>
    );
};

const RetiredTable: FC<{ types: EquipmentType[] }> = ({ types }) => {
    const onRowClick = useRowClick();
    const spaces = useAsyncAtomValue(spacesState, []);
    const allTypes = useAsyncAtomValue(allEquipmentTypesState, []);
    const spaceLabels = new Map(spaces.map((space) => [space.id, spacePillLabel(space)]));
    const titles = new Map(allTypes.map((t) => [t.id, t.title]));

    return (
        <Table className="not-prose mt-4">
            <TableHeader>
                <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Space</TableHead>
                    <TableHead>Retired</TableHead>
                    <TableHead>Moved To</TableHead>
                    <TableHead>Guide</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {orderBy(types, (t) => t.retiredAt, 'desc').map((type) => (
                    <TableRow
                        key={type.id}
                        className="hover:bg-muted cursor-pointer"
                        onClick={onRowClick(`/equipment/types/${type.id}`)}
                    >
                        <TableCell>
                            <div className="flex flex-row items-center gap-2">
                                {type.icon && <AppIcon icon={type.icon} />}
                                <div>
                                    <div>{type.title}</div>
                                    <div className="text-xs text-muted-foreground">{type.id}</div>
                                </div>
                            </div>
                        </TableCell>
                        <TableCell>{spaceLabels.get(type.spaceId) ?? type.spaceId}</TableCell>
                        <TableCell>{type.retiredAt ? formatDate(type.retiredAt) : '-'}</TableCell>
                        <TableCell>
                            {type.replacedBy ? (
                                <Link to={`/equipment/types/${type.replacedBy}`} onClick={(e) => e.stopPropagation()}>
                                    <InfoBadge>→ {titles.get(type.replacedBy) ?? type.replacedBy}</InfoBadge>
                                </Link>
                            ) : (
                                <span className="text-muted-foreground">-</span>
                            )}
                        </TableCell>
                        <TableCell>
                            <GuideCell spaceId={type.spaceId} typeId={type.id} />
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

const GuideCell: FC<{ spaceId: SpaceId; typeId: EquipmentTypeId }> = ({ spaceId, typeId }) => {
    const flowsByTypeId = useAsyncAtomValue(flowsByTypeIdState(spaceId), new Map<EquipmentTypeId, string>());
    return <RowGuide spaceId={spaceId} typeId={typeId} name={flowsByTypeId.get(typeId)} />;
};
